import DigiobcanApi, {
  type NewsFeed,
  type NewsFeedColumn,
} from '../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { getNewsFeeds } from '../../../features/queries/getNewsFeeds';
import { useM } from '../../../lib/query';
import { useLogger } from '../../../providers/LoggerProvider';
import { __, formatDateTime, toastError, toastSuccess } from '../../../utils';
import {
  COLUMN_ROLE_LABEL,
  changesResync,
  columnRoles,
  draftChanges,
  feedDraft,
  feedProgress,
  isBusy,
  orderedColumns,
  relative,
  statusMeta,
  toggleColumnRole,
  useRunNewsFeeds,
  type ColumnRole,
  type FeedDraft,
} from './newsFeeds';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DataTable,
  IconButton,
  MenuItem,
  MeterBar,
  Select,
  Stack,
  StatusChip,
  SwitchField,
  TextInput,
  Typography,
  type Column,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

type Props = {
  /** Živý záznam zo zoznamu - detail sa s ním obnovuje, kým import beží. */
  feed: NewsFeed | null;
  onClose: () => void;
};

type ColumnRow = NewsFeedColumn & { missing?: boolean };

const ROLES: ColumnRole[] = ['category', 'date', 'id', 'title'];

/**
 * Použitie stĺpca. Viac naraz je bežné (Meno je v ID aj v titulku), preto
 * výber s viacerými možnosťami. „Dáta" nie je voľba: parser dáva do popisu
 * novinky každý vyplnený stĺpec bez ohľadu na použitie (`describeItem`
 * v news-feed-parser), takže je vždy zaškrtnutá a nedá sa odškrtnúť. Kým
 * bola voľbou, tvárila sa ako „bez použitia" a zrušila ostatné - stĺpec sa
 * potom nedal mať v ID aj v dátach, hoci v dátach bol stále.
 */
const ColumnRoleSelect: React.FC<{
  value: ColumnRole[];
  onChange: (roles: ColumnRole[]) => void;
}> = ({ value, onChange }) => (
  <Select
    multiple
    displayEmpty
    size='small'
    fullWidth
    value={value}
    onChange={(e) =>
      onChange(
        (typeof e.target.value === 'string'
          ? e.target.value.split(',')
          : e.target.value) as ColumnRole[],
      )
    }
    renderValue={() =>
      value.length
        ? ROLES.filter((r) => value.includes(r))
            .map((r) => COLUMN_ROLE_LABEL[r])
            .join(', ')
        : __('Len dáta')
    }
  >
    {ROLES.map((role) => (
      <MenuItem key={role} value={role}>
        <Checkbox checked={value.includes(role)} />
        {COLUMN_ROLE_LABEL[role]}
      </MenuItem>
    ))}
    <MenuItem disabled>
      <Checkbox checked />
      {__('Dáta - v popise novinky vždy')}
    </MenuItem>
  </Select>
);

const Attribute: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <>
    <Typography
      variant='body2'
      color='text.secondary'
      sx={{ alignSelf: 'center' }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      {typeof children === 'string' ? (
        <Typography variant='body2'>{children}</Typography>
      ) : (
        children
      )}
    </Box>
  </>
);

/** Rovnaká podmienka ako `pattern` v `UpdateNewsFeed` v API. */
const URL_PATTERN = /^https?:\/\/\S+$/;

const timeWithRelative = (iso?: string) =>
  iso ? `${formatDateTime(iso)} (${relative(iso)})` : __('nikdy');

const NewsFeedDialog: React.FC<Props> = ({ feed, onClose }) => {
  const { logger } = useLogger();
  const queryClient = useQueryClient();
  const runFeeds = useRunNewsFeeds();

  // Návrh žije v dialógu. Nastaví sa pri otvorení zdroja, nie pri každom
  // obnovení zoznamu - to beží každé 2 s, kým import prebieha, a prepísalo by
  // rozpísané zmeny. „Zavrieť" dialóg zavrie a pri ďalšom otvorení sa návrh
  // nastaví nanovo zo záznamu.
  const feedId = feed?.feed_id;
  const [draft, setDraft] = React.useState<FeedDraft | null>(null);
  React.useEffect(() => {
    setDraft(feed ? feedDraft(feed) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedId]);

  const saved = feed ? feedDraft(feed) : null;
  const changes = draft && saved ? draftChanges(draft, saved) : {};
  const dirty = Object.keys(changes).length > 0;
  const rekeyed =
    !!draft && !!saved && draft.id_template.trim() !== saved.id_template;
  const categoryEmpty = !!draft && !draft.category_template.trim();
  const urlInvalid = !!draft && !URL_PATTERN.test(draft.url.trim());

  const saveFeed = useM(
    (values: Partial<FeedDraft>) =>
      DigiobcanApi.updateNewsFeed(feedId!, {}, values),
    {
      onSuccess: (updated, values) => {
        toastSuccess(
          changesResync(values)
            ? __('Zdroj je uložený a znova sa synchronizuje.')
            : __('Zdroj je uložený.'),
        );
        queryClient.setQueryData(
          [getNewsFeeds.toString()],
          (old: NewsFeed[] | undefined) =>
            old?.map((f) => (f.feed_id === updated.feed_id ? updated : f)),
        );
        onClose();
      },
      onError: (e, values) => {
        logger.error('Error updating news feed', {
          action: 'updateNewsFeed',
          error: e,
          data: { feedId, values },
        });
        toastError(
          e?.details?.name === 'NEWS_FEED_URL_EXISTS'
            ? __('Túto adresu už sťahuje iný zdroj.')
            : __('Zdroj sa nepodarilo uložiť. Skontrolujte adresu a šablóny.'),
        );
      },
    },
  );

  const isRss = feed?.type === 'RSS';
  const roles = React.useMemo(
    () => (feed && draft ? columnRoles(feed.type, draft) : new Map()),
    [feed, draft],
  );
  // Poradie zo zdroja - podľa neho sa skladá šablóna ID (`toggleColumnRole`).
  // Je to kľúč položky, nie text, takže sa nesmie meniť s poradím popisu:
  // preťiahnutie riadku by inak pri ďalšom prepnutí ID dalo novinkám nové
  // identifikátory.
  const columnOrder = React.useMemo(
    () => (feed?.columns ?? []).map((c) => c.name),
    [feed],
  );

  // Stĺpce v poradí popisu novinky - to, čo sa ťahaním mení. Stĺpec, na
  // ktorý sa šablóna odkazuje a zdroj ho neposlal, sa pridá na koniec ako
  // chýbajúci - to je jediné miesto, kde sa preklep v šablóne dá uvidieť.
  // Chýbajúci sa neťahá: v popise nie je.
  const rows = React.useMemo<ColumnRow[]>(() => {
    const columns = feed?.columns ?? [];
    if (!columns.length) return [];
    const known = new Set(columnOrder);
    return [
      ...orderedColumns(columns, draft?.column_order ?? []),
      ...[...roles.keys()]
        .filter((name) => !known.has(name))
        .map((name) => ({ name, filled: 0, missing: true })),
    ];
  }, [feed, roles, columnOrder, draft?.column_order]);

  /**
   * Presun stĺpca v poradí popisu. Návrh dostane **celé** poradie, nie len
   * presunutý stĺpec - uložené poradie potom nezávisí od toho, ako parser
   * radí zvyšok.
   */
  const moveColumn = React.useCallback(
    (name: string, target: (order: string[]) => number) =>
      setDraft((d) => {
        if (!d || !feed?.columns) return d;
        const order = orderedColumns(feed.columns, d.column_order).map(
          (c) => c.name,
        );
        const from = order.indexOf(name);
        const to = target(order);
        if (from === -1 || to < 0 || to >= order.length || to === from) {
          return d;
        }
        order.splice(from, 1);
        order.splice(to, 0, name);
        return { ...d, column_order: order };
      }),
    [feed],
  );

  /**
   * Ťahanie za úchyt. Cez pointer udalosti, nie HTML5 drag & drop: ten na
   * dotykovom displeji nefunguje a `DataTable` riadku vlastné atribúty
   * nepustí, takže by nebolo kam dať cieľ. Cieľ je riadok, ktorého úchyt je
   * zvislo najbližšie ukazovateľu, a riadok sa presúva hneď počas ťahania -
   * pustenie už len ukončí ťah. Posluchy sú na `window`, lebo riadok sa pod
   * ukazovateľom presúva.
   */
  const handleRefs = React.useRef(new Map<string, HTMLElement>());
  const [dragged, setDragged] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!dragged) return;
    const onMove = (event: PointerEvent) => {
      let nearest: string | undefined;
      let best = Infinity;
      handleRefs.current.forEach((element, name) => {
        const rect = element.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - event.clientY);
        if (distance < best) {
          best = distance;
          nearest = name;
        }
      });
      if (nearest && nearest !== dragged) {
        const target = nearest;
        moveColumn(dragged, (order) => order.indexOf(target));
      }
    };
    const onEnd = () => setDragged(null);
    const userSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    return () => {
      document.body.style.userSelect = userSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
    };
  }, [dragged, moveColumn]);

  const total =
    feed?.last_item_count ??
    Math.max(0, ...(feed?.columns ?? []).map((c) => c.filled));

  // Výberač vráti celú novú sadu; šablóny sa upravia za každú rolu, ktorá
  // pribudla alebo vypadla, tou istou funkciou ako jednotlivé prepnutie.
  const setColumnRoles = React.useCallback(
    (column: string, current: ColumnRole[], next: ColumnRole[]) =>
      setDraft(
        (d) =>
          d &&
          ROLES.filter((r) => current.includes(r) !== next.includes(r)).reduce(
            (acc, role) => toggleColumnRole(acc, role, column, columnOrder),
            d,
          ),
      ),
    [columnOrder],
  );

  const columns: Column<ColumnRow>[] = React.useMemo(
    () => [
      // RSS popis neskladá zo stĺpcov (je to `summary` položky), takže tam
      // poradie nemá čo meniť a úchyt by klamal.
      ...(isRss
        ? []
        : [
            {
              key: 'order',
              label: '',
              width: '36px',
              minWidth: '36px',
              render: (row: ColumnRow) =>
                row.missing ? null : (
                  <Box
                    ref={(element: HTMLElement | null) => {
                      if (element) handleRefs.current.set(row.name, element);
                      else handleRefs.current.delete(row.name);
                    }}
                    role='button'
                    tabIndex={0}
                    aria-label={`${__('Presunúť stĺpec')} ${row.name}`}
                    title={__('Potiahnutím zmeníte poradie v popise novinky')}
                    onPointerDown={(event: React.PointerEvent) => {
                      event.preventDefault();
                      setDragged(row.name);
                    }}
                    // Klávesnicou šípkami o riadok - ťahanie myšou nie je
                    // jediná cesta.
                    onKeyDown={(event: React.KeyboardEvent) => {
                      const step =
                        event.key === 'ArrowUp'
                          ? -1
                          : event.key === 'ArrowDown'
                            ? 1
                            : 0;
                      if (!step) return;
                      event.preventDefault();
                      moveColumn(
                        row.name,
                        (order) => order.indexOf(row.name) + step,
                      );
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                      cursor: dragged ? 'grabbing' : 'grab',
                      // Bez toho by prehliadač na dotyk namiesto ťahu
                      // posúval dialóg.
                      touchAction: 'none',
                      opacity: dragged && dragged !== row.name ? 0.5 : 1,
                    }}
                  >
                    <DragIndicatorIcon fontSize='small' />
                  </Box>
                ),
            },
          ]),
      {
        key: 'name',
        label: __('Stĺpec'),
        width: '1.2fr',
        minWidth: '120px',
        // Písmo ako v ostatných stĺpcoch a zalomenie kdekoľvek: názvy z eGovu
        // nemajú medzery (`IČO_-_Identifikačné_číslo_dodávateľa`), takže bez
        // `anywhere` by text vytiekol pod výberač v susednom stĺpci.
        render: (row) => (
          <Typography
            variant='body2'
            color={row.missing ? 'error' : undefined}
            sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
          >
            {row.name}
          </Typography>
        ),
      },
      {
        key: 'roles',
        label: __('Použitie'),
        width: '2fr',
        minWidth: '180px',
        render: (row) => {
          const active: ColumnRole[] = roles.get(row.name) ?? [];
          // RSS číta parser pevne, tam sa použitie nedá meniť - len štítky.
          if (isRss) {
            return active.length ? (
              <Stack direction='row' gap={0.5} flexWrap='wrap'>
                {active.map((role) => (
                  <StatusChip key={role} tone='info'>
                    {COLUMN_ROLE_LABEL[role]}
                  </StatusChip>
                ))}
              </Stack>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                {__('Dáta')}
              </Typography>
            );
          }
          return (
            <ColumnRoleSelect
              value={active}
              onChange={(next) => setColumnRoles(row.name, active, next)}
            />
          );
        },
      },
      {
        key: 'filled',
        label: __('Vyplnené'),
        width: '0.8fr',
        minWidth: '90px',
        // Farba povie na prvý pohľad, či sa na stĺpec dá spoľahnúť: prázdny
        // je na šablónu nepoužiteľný, čiastočne vyplnený dá niektorým
        // novinkám prázdne miesto (titulok, dátum).
        render: (row) => (
          <StatusChip
            tone={
              row.missing || !row.filled
                ? 'error'
                : row.filled < total
                  ? 'warning'
                  : 'success'
            }
          >
            {row.missing
              ? __('chýba')
              : `${row.filled.toLocaleString('sk')}/${total.toLocaleString('sk')}`}
          </StatusChip>
        ),
      },
      {
        key: 'sample',
        label: __('Ukážka'),
        width: '1.8fr',
        minWidth: '100px',
        render: (row) => (
          <Typography
            variant='body2'
            color={row.missing ? 'error' : 'text.secondary'}
            title={row.sample}
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.missing
              ? __('Šablóna sa naň odkazuje, zdroj ho neposlal')
              : row.sample}
          </Typography>
        ),
      },
    ],
    [roles, isRss, setColumnRoles, total, dragged, moveColumn],
  );

  const status = feed ? statusMeta(feed) : undefined;
  const progress = feed ? feedProgress(feed) : undefined;
  const busy = feed ? isBusy(feed) : false;

  const templateField = (
    key: 'category_template' | 'id_template' | 'title_template',
    empty: string,
  ) => (
    <TextInput
      mono
      value={draft?.[key] ?? ''}
      placeholder={empty}
      invalid={key === 'category_template' && categoryEmpty}
      errorText={__('Šablóna kategórie nesmie byť prázdna')}
      onChange={(e) => setDraft((d) => d && { ...d, [key]: e.target.value })}
    />
  );

  const content =
    feed && draft ? (
      <Stack gap={2}>
        {/* Len stav: zapnutie a notifikácie sú prepínače nižšie, v štítkoch
              by „Vypnutý" stál dvakrát (stav vypnutého zdroja je Vypnutý). */}
        {status ? (
          <Stack gap={0.75}>
            <Box>
              <StatusChip tone={status.tone}>{status.label}</StatusChip>
            </Box>
            {progress ? (
              <Stack gap={0.5}>
                <Typography variant='body2' color='text.secondary'>
                  {progress.text}
                </Typography>
                {progress.percent !== undefined ? (
                  <MeterBar value={progress.percent} minVisible={2} />
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        ) : null}

        {feed.last_status === 'error' && feed.last_error ? (
          <Alert severity='error'>{feed.last_error}</Alert>
        ) : null}

        {!feed.mapped ? (
          <Alert severity='warning'>
            {__(
              'Zdroj nepreberá žiadna kategória, jeho novinky sa v Môj Prešov nezobrazujú. Sťahuje sa ďalej - novinky môže odoberať aj niekto iný. Zaradíte ho v detaile kategórie pridaním jeho zdrojovej kategórie.',
            )}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1,
          }}
        >
          <SwitchField
            label={__('Zdroj je zapnutý')}
            description={__(
              'Plánovač ho sťahuje raz za hodinu. Vypnutý sa dá stiahnuť ručne.',
            )}
            checked={draft.enabled}
            onChange={(enabled) => setDraft((d) => d && { ...d, enabled })}
          />
          <SwitchField
            label={__('Posiela notifikácie')}
            description={__(
              'Nové novinky zo zdroja dostanú odberatelia ich kategórií.',
            )}
            checked={draft.notify}
            onChange={(notify) => setDraft((d) => d && { ...d, notify })}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
            columnGap: 2,
            rowGap: { xs: 0.25, sm: 1 },
          }}
        >
          <Attribute label={__('Typ')}>
            {isRss ? 'RSS' : __('eGov (JSON)')}
          </Attribute>
          <Attribute label={__('Zdroj')}>
            <Stack direction='row' alignItems='center' gap={0.5}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextInput
                  value={draft.url}
                  invalid={urlInvalid}
                  errorText={__('Adresa musí začínať http:// alebo https://')}
                  onChange={(e) =>
                    setDraft((d) => d && { ...d, url: e.target.value })
                  }
                />
              </Box>
              {/* Uložená adresa, nie rozpísaná - tá nemusí byť platná. */}
              <IconButton
                size='small'
                onClick={() => window.open(feed.url, '_blank', 'noopener')}
                aria-label={__('Otvoriť zdroj v novej karte')}
                title={__('Otvoriť zdroj v novej karte')}
              >
                <OpenInNewIcon fontSize='small' />
              </IconButton>
            </Stack>
          </Attribute>
          <Attribute label={__('Šablóna kategórie')}>
            {templateField('category_template', '')}
          </Attribute>
          {!isRss ? (
            <>
              <Attribute label={__('Šablóna ID')}>
                {templateField(
                  'id_template',
                  __('bez šablóny - ID je odtlačok celej položky'),
                )}
              </Attribute>
              <Attribute label={__('Šablóna titulku')}>
                {templateField(
                  'title_template',
                  __('bez šablóny - prvý riadok popisu'),
                )}
              </Attribute>
            </>
          ) : null}
          <Attribute label={__('Posledný beh')}>
            {timeWithRelative(feed.last_success_at)}
          </Attribute>
          <Attribute label={__('Ďalší plánovaný beh')}>
            {feed.enabled
              ? timeWithRelative(feed.next_run_at)
              : __('zdroj je vypnutý')}
          </Attribute>
          <Attribute label={__('Položiek vo zdroji')}>
            {feed.last_item_count?.toLocaleString('sk') ?? '-'}
          </Attribute>
        </Box>

        {rekeyed ? (
          <Alert severity='warning'>
            {__(
              'Iná šablóna ID dá položkám nové identifikátory: novinky zdroja sa pri synchronizácii vytvoria nanovo. Tá synchronizácia nepošle notifikácie.',
            )}
          </Alert>
        ) : null}

        <Stack gap={1}>
          <Stack direction='row' alignItems='center' gap={1}>
            <Typography variant='subtitle2' sx={{ flex: 1 }}>
              {__('Stĺpce zdroja')}
            </Typography>
            {!isRss && draft.column_order.length ? (
              <Button
                variant='text'
                size='small'
                onClick={() => setDraft((d) => d && { ...d, column_order: [] })}
              >
                {__('Zoradiť podľa abecedy')}
              </Button>
            ) : null}
          </Stack>
          {!isRss && rows.length ? (
            <Typography variant='caption' color='text.secondary'>
              {__(
                'Použitie stĺpca ho pridá do šablóny kategórie, ID alebo titulku, prípadne ho zvolí ako dátum. V popise novinky (Dáta) je každý vyplnený stĺpec vždy, aj keď má iné použitie, a to v poradí tejto tabuľky - zmeníte ho potiahnutím za úchyt. Oddeľovače sa dajú doladiť v šablónach vyššie.',
              )}
            </Typography>
          ) : null}
          {rows.length ? (
            <DataTable
              variant='table'
              columns={columns}
              rows={rows}
              rowKey='name'
            />
          ) : (
            <Typography variant='body2' color='text.secondary'>
              {__(
                'Stĺpce sa zistia pri najbližšej úspešnej synchronizácii zdroja.',
              )}
            </Typography>
          )}
        </Stack>
      </Stack>
    ) : null;

  return (
    <ConfirmationDialog
      open={!!feed}
      title={feed?.name}
      // Uloženie a synchronizácia sú dve rôzne veci, preto na dvoch stranách
      // pätičky: napravo nastavenie zdroja (Zavrieť / Uložiť), naľavo stiahnutie
      // s uloženým nastavením. Zmena adresy či šablón po uložení zdroj
      // synchronizuje sama (API), takže tlačidlo naľavo treba len na ručný beh.
      buttons={
        <Button
          variant='outline'
          startIcon={<SyncIcon />}
          disabled={busy || runFeeds.isPending || saveFeed.isPending}
          loading={runFeeds.isPending}
          onClick={() => feed && runFeeds.mutate(feed.feed_id)}
        >
          {busy ? __('Synchronizácia prebieha') : __('Synchronizovať')}
        </Button>
      }
      closeLabel={__('Zavrieť')}
      onClose={onClose}
      confirmLabel={__('Uložiť')}
      confirmDisabled={!dirty || categoryEmpty || urlInvalid}
      isLoading={saveFeed.isPending}
      onConfirm={() => saveFeed.mutate(changes)}
      fullWidth
      maxWidth='md'
    >
      {content}
    </ConfirmationDialog>
  );
};

export default NewsFeedDialog;
